import { PerformanceMetric, usePerformance } from '@/context/performance';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useEffect, useRef, useState } from 'react';
import { AppBundle } from '../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { plumaContextValueStore, plumaThemeStore } from '@/components/connected-pluma-provider';

export default function DesignStudio(props?: { basename?: string }) {
  return <DesignStudioBackgroundApp basename={props?.basename} />;
}

export function DesignStudioNonBackgroundApp(props?: { basename?: string }) {
  const ref = useRef(null);
  const rendered = useRef(false);
  const root = useRef<unknown | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();

  useEffect(() => {
    setChildAppLoaded(false);
  }, []);

  useEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      const { rootComponent: DesignStudioApp, ReactDOMClient: DesignStudioReactDomClient } = (await window.importShim(
        AppBundle.DesignStudio,
      )) as { rootComponent: unknown; ReactDOMClient: unknown };
      setIsLoaded(true);

      if (ref.current == null) {
        return;
      }

      if (root.current == null) {
        root.current = DesignStudioReactDomClient.createRoot(ref.current, { identifierPrefix: 'design_studio_' });
      }

      if (!rendered.current && root.current != null) {
        root.current.render(
          <DesignStudioApp
            basename={props?.basename}
            plumaContextValue={plumaContextValueStore}
            plumaTheme={plumaThemeStore}
          />,
        );
        rendered.current = true;
      }
    };

    load();

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;

        rendered.current = false;
        setIsLoaded(false);
      }
    };
  }, []);

  return (
    <>
      <div ref={ref} style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1 }}></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}

export function DesignStudioBackgroundApp(props?: { basename?: string }) {
  const rendered = useRef(false);
  const root = useRef<unknown | null>(null);
  const renderDesignStudioApp = async (appWrapper: HTMLDivElement) => {
    const { rootComponent: DesignStudioApp, ReactDOMClient: DesignStudioReactDomClient } = (await window.importShim(
      AppBundle.DesignStudio,
    )) as { rootComponent: unknown; ReactDOMClient: unknown };

    if (root.current == null) {
      root.current = DesignStudioReactDomClient.createRoot(appWrapper, { identifierPrefix: 'design_studio_' });
    }

    if (!rendered.current && root.current != null) {
      root.current.render(
        <DesignStudioApp
          basename={props?.basename}
          plumaContextValue={plumaContextValueStore}
          plumaTheme={plumaThemeStore}
        />,
      );
      rendered.current = true;
    }

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;
        rendered.current = false;
      }
    };
  };

  return (
    <AppBundleLoader
      appBundle={AppBundle.DesignStudio}
      className="design-studio-app"
      renderApp={renderDesignStudioApp}
    />
  );
}
